import React from 'react';
import {
  Calendar, CheckCircle2, Clock, Hash, IndianRupee, Mail, MapPin, Stethoscope, Ticket,
} from 'lucide-react';
import { IITH, SERIF, SANS } from '@/pages/landing/iithBrand';
import { formatDateIST } from '@/utils/date';

function Row({ icon: Icon, label, value, mono }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-start gap-3 rounded-xl bg-white/[0.04] border border-white/10 px-3.5 py-3">
      <div className="mt-0.5 h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: `${IITH.saffron}22`, color: IITH.saffron }}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-[0.16em] text-[#9AADBE]" style={{ fontFamily: SANS }}>{label}</p>
        <p className={`mt-0.5 text-sm text-white ${mono ? 'font-mono font-semibold tracking-wide' : 'font-semibold'}`}
          style={{ fontFamily: mono ? undefined : SANS }}>
          {value}
        </p>
      </div>
    </div>
  );
}

function feeLabel(n) {
  return `₹${Number(n || 0).toLocaleString('en-IN')}`;
}

/** Confirmed/booked success panel — payment instructions when fee is due. */
export default function BookingSuccessCard({ success }) {
  const fee = Number(success.suggested_fee || 0);
  const payDue = Boolean(success.payment_required) || fee > 0;
  const dateLabel = success.preferred_date || success.date
    ? formatDateIST(success.preferred_date || success.date) : '';
  const slot = success.preferred_slot || success.slot || '';
  const slotLabel = slot && slot !== 'day' ? slot : (slot === 'day' ? 'As scheduled' : '');
  const name = success.patient_name || success.name || '';

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-[#C4A35A]/35 shadow-2xl"
      style={{ background: `linear-gradient(160deg, ${IITH.navy} 0%, ${IITH.navyMid} 55%, #0a1f33 100%)` }}
      data-testid="booking-success"
    >
      <div className="h-1.5" style={{ background: `linear-gradient(90deg, ${IITH.saffron}, ${IITH.gold}, ${IITH.saffron})` }} />
      <div className="p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row sm:items-start gap-5">
          <div className="mx-auto sm:mx-0 h-16 w-16 rounded-full flex items-center justify-center shrink-0"
            style={{
              background: payDue
                ? 'linear-gradient(145deg,#f59e0b,#c2410c)'
                : 'linear-gradient(145deg,#22c55e,#15803d)',
              boxShadow: payDue
                ? '0 0 0 6px rgba(245,158,11,0.18)'
                : '0 0 0 6px rgba(34,197,94,0.18)',
            }}>
            <CheckCircle2 className="h-9 w-9 text-white" />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: IITH.gold, fontFamily: SANS }}>
              IIT Hyderabad Hospital
            </p>
            <h2 className="text-2xl sm:text-3xl text-white mt-1" style={{ fontFamily: SERIF }}>
              {payDue ? 'Appointment booked' : 'Appointment confirmed'}
            </h2>
            <p className="mt-2 text-sm text-[#B8C7D6]" style={{ fontFamily: SANS }}>
              {name ? `Hello ${name} — ` : ''}
              {payDue
                ? 'your OPD slot is reserved. Pay at the hospital to complete confirmation.'
                : 'your OPD visit is confirmed. A QR code was sent to your email.'}
            </p>
          </div>
        </div>

        {payDue ? (
          <div
            className="mt-5 rounded-xl border border-amber-400/40 bg-amber-500/15 px-4 py-4"
            data-testid="booking-payment-notice"
          >
            <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-amber-300" style={{ fontFamily: SANS }}>
              Payment at the hospital
            </p>
            <p className="mt-1 text-2xl font-bold text-white" style={{ fontFamily: SANS }}>
              {feeLabel(fee)}
            </p>
            <p className="mt-2 text-[13px] text-amber-100/90 leading-relaxed" style={{ fontFamily: SANS }}>
              Your appointment is <strong className="text-white">booked</strong>.
              Pay the consultation fee at reception when you arrive.
              It is <strong className="text-white">fully confirmed only after payment</strong>
              (and triage) at the hospital. Cash, UPI and card are accepted.
            </p>
          </div>
        ) : null}

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          <Row icon={Hash} label="Patient number" value={success.patient_number} mono />
          <Row icon={Ticket} label="Appointment number" value={success.appointment_number} mono />
          <Row icon={Stethoscope} label="Doctor" value={success.doctor_name || success.doctor} />
          <Row icon={Calendar} label="Date" value={dateLabel} />
          <Row icon={Clock} label="Time slot" value={slotLabel} />
          <Row icon={Ticket} label="Queue token" value="Issued after triage (emailed)" />
          {payDue ? <Row icon={IndianRupee} label="Fee due at hospital" value={feeLabel(fee)} /> : null}
        </div>

        <div className="mt-5 rounded-xl border border-white/10 bg-black/20 px-4 py-3.5 space-y-2">
          <p className="text-[10px] uppercase tracking-[0.16em] font-bold" style={{ color: IITH.saffron, fontFamily: SANS }}>
            What to do next
          </p>
          <ol className="text-[12px] text-[#C5D0DB] space-y-1.5 list-decimal list-inside" style={{ fontFamily: SANS }}>
            <li>Arrive 10 minutes early with a valid photo ID</li>
            <li className="flex gap-2 list-none ml-0"><Mail className="h-3.5 w-3.5 mt-0.5 shrink-0 text-emerald-400" /> Show the email QR at reception check-in</li>
            {payDue ? <li>Pay {feeLabel(fee)} at the desk — then your visit is confirmed</li> : null}
            <li>Complete triage — your queue number is issued then and emailed to you</li>
            <li className="flex gap-2 list-none ml-0"><MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0 text-emerald-400" /> {IITH.campus}</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
